import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MarketplaceLayout } from "../components/MarketplaceLayout";
import { useI18n } from "../contexts/I18nContext";
import api, { fileUrl } from "../lib/api";
import { MapPin, Phone, Mail, Instagram, Globe, ArrowLeft } from "lucide-react";

export default function WorkshopsPage() {
  const { t } = useI18n();
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cityFilter, setCityFilter] = useState("all");

  useEffect(() => {
    api.get("/workshops")
      .then((r) => setWorkshops(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const cities = Array.from(new Set(workshops.map((w) => w.city).filter(Boolean))).sort();
  const filtered = cityFilter === "all" ? workshops : workshops.filter((w) => w.city === cityFilter);

  return (
    <MarketplaceLayout>
      {/* Hero */}
      <section className="border-b border-black/5 py-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Link to="/" className="inline-flex items-center gap-2 overline text-neutral-500 hover:text-black transition-colors mb-8" data-testid="back-home">
            <ArrowLeft size={14} strokeWidth={1.5} /> {t.nav.shop}
          </Link>
          <div className="overline text-neutral-500 mb-4">N° 04 · Nuestros aliados</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl">
            Talleres de enmarcación
          </h1>
          <p className="text-base text-neutral-600 mt-6 max-w-2xl leading-relaxed">
            Compra la obra en <span className="italic">d-Pod</span> y remátala con un marco a medida en el
            enmarcador local que elijas. Tras tu compra, el taller recibe la copia listada y tú lo recoges
            enmarcado — sin envíos extra, sin intermediarios.
          </p>
        </div>
      </section>

      {/* Filters + Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          {cities.length > 1 && (
            <div className="flex flex-wrap items-center gap-x-1 gap-y-2 mb-12 overline text-[11px]">
              <span className="text-neutral-400 mr-3">Ciudad</span>
              <button
                onClick={() => setCityFilter("all")}
                className={`px-3 py-1.5 border transition-colors ${cityFilter === "all" ? "bg-neutral-950 border-neutral-950 text-white" : "border-neutral-200 text-neutral-500 hover:border-neutral-900 hover:text-neutral-900"}`}
                data-testid="city-all"
              >
                Todas ({workshops.length})
              </button>
              {cities.map((c) => (
                <button
                  key={c}
                  onClick={() => setCityFilter(c)}
                  className={`px-3 py-1.5 border transition-colors ${cityFilter === c ? "bg-neutral-950 border-neutral-950 text-white" : "border-neutral-200 text-neutral-500 hover:border-neutral-900 hover:text-neutral-900"}`}
                  data-testid={`city-${c}`}
                >
                  {c} ({workshops.filter((w) => w.city === c).length})
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="py-32 text-center overline text-neutral-400">Cargando…</div>
          ) : filtered.length === 0 ? (
            <div className="py-32 text-center overline text-neutral-400" data-testid="workshops-empty">
              Aún no hay talleres asociados{cityFilter !== "all" ? ` en ${cityFilter}` : ""}.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16" data-testid="workshops-grid">
              {filtered.map((w) => (
                <article key={w.id} className="group" data-testid={`workshop-${w.id}`}>
                  <div className="aspect-[4/3] bg-neutral-100 overflow-hidden">
                    {w.picture ? (
                      <img
                        src={fileUrl(w.picture, "md")}
                        alt={w.name}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-neutral-300">
                        <MapPin size={40} strokeWidth={1} />
                      </div>
                    )}
                  </div>
                  <div className="mt-6">
                    <div className="overline text-neutral-400 mb-2">{w.city}{w.postal_code ? ` · ${w.postal_code}` : ""}</div>
                    <h3 className="font-serif text-2xl leading-tight">{w.name}</h3>
                    {w.description && (
                      <p className="text-sm text-neutral-600 mt-3 leading-relaxed line-clamp-3">{w.description}</p>
                    )}
                    <div className="mt-5 space-y-2 text-sm text-neutral-700">
                      <div className="flex items-start gap-2">
                        <MapPin size={14} strokeWidth={1.5} className="mt-0.5 shrink-0 text-neutral-400" />
                        <span>{w.address}</span>
                      </div>
                      {w.phone && (
                        <a href={`tel:${w.phone}`} className="flex items-center gap-2 hover:text-black transition-colors" data-testid={`workshop-phone-${w.id}`}>
                          <Phone size={14} strokeWidth={1.5} className="shrink-0 text-neutral-400" />
                          <span>{w.phone}</span>
                        </a>
                      )}
                      {w.email && (
                        <a href={`mailto:${w.email}`} className="flex items-center gap-2 hover:text-black transition-colors" data-testid={`workshop-email-${w.id}`}>
                          <Mail size={14} strokeWidth={1.5} className="shrink-0 text-neutral-400" />
                          <span className="truncate">{w.email}</span>
                        </a>
                      )}
                      {w.website && (
                        <a href={w.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-black transition-colors" data-testid={`workshop-web-${w.id}`}>
                          <Globe size={14} strokeWidth={1.5} className="shrink-0 text-neutral-400" />
                          <span className="truncate">{w.website.replace(/^https?:\/\//, "")}</span>
                        </a>
                      )}
                      {w.instagram && (
                        <a href={`https://instagram.com/${w.instagram.replace(/^@/, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-black transition-colors" data-testid={`workshop-ig-${w.id}`}>
                          <Instagram size={14} strokeWidth={1.5} className="shrink-0 text-neutral-400" />
                          <span>{w.instagram.startsWith("@") ? w.instagram : `@${w.instagram}`}</span>
                        </a>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}

          {/* CTA — invite framers to join */}
          <div className="mt-24 lg:mt-32 border-t border-black/5 pt-16 grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7">
              <div className="overline text-neutral-500 mb-4">¿Eres enmarcador?</div>
              <h2 className="font-serif text-3xl lg:text-4xl leading-tight tracking-tight">
                Únete al mapa de d-Pod Gallery
              </h2>
              <p className="text-base text-neutral-600 mt-4 max-w-xl leading-relaxed">
                Recibimos compradores de arte impreso en tu zona cada semana. Enlazamos tu taller
                directamente en el paso post-compra: sin comisión, sin CRM. Escríbenos a{" "}
                <a href="mailto:hello@d-pod.gallery" className="text-neutral-900 underline">hello@d-pod.gallery</a>{" "}
                con tus datos y una foto del local.
              </p>
            </div>
            <div className="lg:col-span-5 bg-neutral-50 p-8 lg:p-10">
              <div className="overline text-neutral-500 mb-4">Cómo funciona</div>
              <ol className="text-sm text-neutral-700 space-y-3 leading-relaxed">
                <li className="flex gap-3"><span className="font-serif text-lg text-neutral-400">1.</span> El cliente compra una obra en d-Pod y elige su ciudad.</li>
                <li className="flex gap-3"><span className="font-serif text-lg text-neutral-400">2.</span> En la confirmación del pedido se le muestran los talleres locales asociados.</li>
                <li className="flex gap-3"><span className="font-serif text-lg text-neutral-400">3.</span> El cliente contacta directamente al taller. Sin comisión para d-Pod.</li>
                <li className="flex gap-3"><span className="font-serif text-lg text-neutral-400">4.</span> Impresión hecha por <span className="italic">d-Impresión</span> · marco hecho por ti.</li>
              </ol>
            </div>
          </div>
        </div>
      </section>
    </MarketplaceLayout>
  );
}
